import styled, { css } from 'styled-components';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';
import { DEEP_BORDER_BOX } from 'common';

const {
  SPACING,
  RADIUS,
  TYPOGRAPHY,
  LINE_HEIGHT,
  FONT_FAMILY
} = Tokens;

// font-weight doesn't change the thickness, so NOKIA_PURE_TEXT_BOLD is used
const BUTTON_TYPOGRAPHY = {
  ...TYPOGRAPHY.BODY,
  fontFamily: FONT_FAMILY.NOKIA_PURE_TEXT_BOLD,
  lineHeight: LINE_HEIGHT.LINE_HEIGHT_NONE,
};

const BUTTON_HEIGHT = '2rem';
const BUTTON_INNER_SHADOW_WIDTH = '0.125rem';
const OUTLINED_BUTTON_INNER_SHADOW_WIDTH = '0.063rem';

const BUTTON_MIN_WIDTH = '4rem';

const getBorder = (color, width = BUTTON_INNER_SHADOW_WIDTH) => css`box-shadow: inset 0 0 0 ${width} ${color};`;

const BUTTON_STYLE = css`
  ${DEEP_BORDER_BOX}
  direction: ${props => (props.RTL || props.$rtl || props.rtl) ? 'rtl' : 'ltr'};
  background-color: ${props => props.$theme.container};
  position: relative;
  display: inline-block;
  outline: none;
  cursor: pointer;
  overflow: hidden;
  height: ${BUTTON_HEIGHT};
  min-width: ${BUTTON_MIN_WIDTH};
  max-width: 100%;
  flex: 0 0 auto;
  margin: 0;
  padding: 0;
  border: 0;
  border-radius: ${RADIUS.RADIUS_2};
  // Gap between icon and text
  & [data-parent="button"] {
    ${props => css`margin: ${(props.RTL || props.$rtl || props.rtl) ? `0 0 0 ${SPACING.SPACING_4}` : `0 ${SPACING.SPACING_4} 0 0`};`}
  }
  ${({ $fullWidth }) => $fullWidth && css`
      width: 100%;
    `}
`;

const BUTTON_FOCUSED = css`
  :focus {
    outline: none;
    ${getBorder(props => props.$theme.focused.border)}
  }
`;

const BUTTON_HOVERED = css`
  :hover:not(:disabled) {
    outline: none;
    background-color: ${props => props.$theme.hovered.container};
  }
`;

const BUTTON_DISABLED = css`
  :disabled {
    cursor: initial;
    background-color: ${props => props.$theme.disabled.container};
    color: ${props => props.$theme.disabled.content};
    & * {
      color: ${props => props.$theme.disabled.content};
    }
    & [data-parent="button"] * {
      fill: ${props => props.$theme.disabled.content};
    }
  }
`;

const OUTLINED_BUTTON_DISABLED = css`
  :disabled {
    cursor: initial;
    background-color: ${props => props.$theme.disabled.container};
    color: ${props => props.$theme.disabled.content};
    ${getBorder(props => props.$theme.disabled.border, OUTLINED_BUTTON_INNER_SHADOW_WIDTH)}
    & * {
      color: ${props => props.$theme.disabled.content};
    }
    & [data-parent="button"] * {
      fill: ${props => props.$theme.disabled.content};
    }
  }
`;

const CTA_BUTTON_FOCUSED = css`
  :focus {
    outline: none;
    ${getBorder(props => props.$theme.focused.border)}
  }
`;

const CTA_BUTTON_HOVERED = css`
  :hover:not(:disabled) {
    outline: none;
    background-color: ${props => props.$theme.hovered.container};
  }
`;

const CTA_BUTTON_DISABLED = css`
  :disabled {
    cursor: initial;
    background-color: ${props => props.$theme.disabled.container};
    & * {
      color: ${props => props.$theme.disabled.content};
    }
    & [data-parent="button"] * {
      fill: ${props => props.$theme.disabled.content};
    }
  }
`;

export default {
  Button: styled.button`
    ${BUTTON_STYLE}
    & [data-parent="button"] * {
      fill: ${props => props.$theme.content};
    }
    & [data-parent="button-text"] {
      color: ${props => props.$theme.content};
    }
    ${BUTTON_FOCUSED}
    ${BUTTON_HOVERED}
    ${BUTTON_DISABLED}
  `,
  CTAButton: styled.button`
    ${BUTTON_STYLE}
    & [data-parent="button"] * {
      fill: ${props => props.$theme.content};
    }
    & [data-parent="button-text"] {
      color: ${props => props.$theme.content};
    }
    ${CTA_BUTTON_FOCUSED}
    ${CTA_BUTTON_HOVERED}
    ${CTA_BUTTON_DISABLED}
  `,
  OutlinedButton: styled.button`
    ${BUTTON_STYLE}
    ${getBorder(props => props.$theme.border, OUTLINED_BUTTON_INNER_SHADOW_WIDTH)}
    & [data-parent="button"] * {
      fill: ${props => props.$theme.content};
    }
    & [data-parent="button-text"] {
      color: ${props => props.$theme.content};
    }
    ${BUTTON_FOCUSED}
    ${BUTTON_HOVERED}
    ${OUTLINED_BUTTON_DISABLED}
  `,
  ButtonText: styled.span`
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    ${BUTTON_TYPOGRAPHY};
  `,
  ExpansionButton: styled.div`
    cursor: pointer;
    ${props => css`direction: ${(props.RTL || props.$rtl || props.rtl) ? 'rtl' : 'ltr'};`}
    display: flex;
    flex-direction: row;
    align-items: center;
    user-select: none;
    & * {
      user-select: none;
    }
  `,
  ExpansionIconWrapper: styled.div`
    margin-left: ${props => (props.RTL || props.$rtl || props.rtl) ? Tokens.SPACING.SPACING_0 : Tokens.SPACING.SPACING_8};
    margin-right: ${props => (props.RTL || props.$rtl || props.rtl) ? Tokens.SPACING.SPACING_8 : Tokens.SPACING.SPACING_0};
  `
};
